import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { SaveLinkRequest, LinkResponse, ListLinksResponse } from '@readlater/shared';

interface UseAddLinkOptions {
  onSuccess?: (link: LinkResponse) => void;
  onError?: (error: Error) => void;
}

export function useAddLink(options?: UseAddLinkOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SaveLinkRequest) => {
      return api.post<LinkResponse>('/links', data);
    },

    // Optimistic update - add pending link immediately
    onMutate: async (newLink) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['links'] });

      // Snapshot previous value
      const previousLinks = queryClient.getQueryData<ListLinksResponse>(['links']);

      // Optimistically update with a temporary link
      if (previousLinks) {
        const tempLink: LinkResponse = {
          id: Date.now(), // Temporary ID
          url: newLink.url,
          source_domain: new URL(newLink.url).hostname,
          favicon_url: `https://www.google.com/s2/favicons?domain=${new URL(newLink.url).hostname}&sz=32`,
          title: null,
          content: null,
          word_count: null,
          reading_time: null,
          summary: null,
          tags: null,
          content_type: null,
          key_quotes: null,
          status: 'pending',
          error_message: null,
          is_read: false,
          is_archived: false,
          is_favorite: false,
          created_at: new Date().toISOString(),
          processed_at: null,
          read_at: null,
          archived_at: null,
        };

        queryClient.setQueryData<ListLinksResponse>(['links'], {
          ...previousLinks,
          links: [tempLink, ...previousLinks.links],
        });
      }

      return { previousLinks };
    },

    // Revert on error
    onError: (error, _variables, context) => {
      if (context?.previousLinks) {
        queryClient.setQueryData(['links'], context.previousLinks);
      }
      options?.onError?.(error);
    },

    // Refetch after success to get the real data
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },

    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
    },
  });
}
